import { moneefloApi } from "./client";

/**
 * The reporting module. Xano kept these behind an Appsmith app the team
 * logged into separately; they live in the portal now, one page with a
 * tab per report.
 */

export type PayoutPartner = {
  id: string;
  name: string;
  /** Always 2: MoneeFlo settles with its L2 partners and nobody else. */
  level: number | null;
  city: string | null;
  status: string;
};

export type PayoutRow = {
  /** "<orderNo>:<partnerId>" — what the mark endpoint takes back. */
  id: string;
  orderNo: string;
  placedAt: string | null;
  orderStatus: string;
  agent: string | null;
  branch: string | null;
  customer: string | null;
  purpose: string | null;
  category: string | null;
  currency: string | null;
  fxAmountMinor: string | null;
  inrAmountMinor: string | null;
  partner: string;
  partnerId: string;
  grossMinor: string;
  tdsMinor: string;
  netMinor: string;
  /** MoneeFlo's own margin. Absent unless the caller may see cost. */
  myProfitMinor?: string;
  status: string;
  billed: boolean;
  paid: boolean;
};

export type PayoutReport = {
  from: string;
  till: string;
  page: number;
  perPage: number;
  total: number;
  summary: {
    orderCount: number;
    grossMinor: string;
    tdsMinor: string;
    netMinor: string;
    myProfitMinor?: string;
    pendingCount: number;
    pendingNetMinor: string;
  };
  items: PayoutRow[];
};

export type PayoutFilters = {
  from?: string;
  till?: string;
  partnerId?: string;
  category?: string;
  status?: string;
  billed?: boolean;
  paid?: boolean;
  page?: number;
  perPage?: number;
};

/**
 * The partners in the current view, not every partner who ever earned.
 * Takes the same filters as the table so the list and the rows agree.
 */
export async function listPayoutPartners(
  filters: PayoutFilters = {},
): Promise<PayoutPartner[]> {
  const { data } = await moneefloApi.get("/reports/payout/partners", {
    params: toParams(filters),
  });
  return data;
}

/**
 * An empty filter must not become a filter, so only what was asked for
 * is sent. Booleans are stringified because that is what the query
 * schema reads.
 */
function toParams(filters: PayoutFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === "") continue;
    params[key] = typeof value === "boolean" ? String(value) : value;
  }
  return params;
}

export async function payoutReport(
  filters: PayoutFilters,
): Promise<PayoutReport> {
  const { data } = await moneefloApi.get("/reports/payout", {
    params: toParams(filters),
  });
  return data;
}

/**
 * Downloads the current view.
 *
 * Fetched rather than navigated to, because the request needs the
 * bearer token; the filename comes from the server so the file is named
 * after the window it covers.
 */
export async function downloadPayoutCsv(filters: PayoutFilters): Promise<void> {
  const response = await moneefloApi.get("/reports/payout/export", {
    params: toParams(filters),
    responseType: "blob",
  });
  const disposition = String(response.headers["content-disposition"] ?? "");
  const named = /filename="?([^"]+)"?/.exec(disposition);
  const url = URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = named?.[1] ?? "payout.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately cancels the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function markPayout(body: {
  ids: string[];
  billed?: boolean;
  paid?: boolean;
}): Promise<{ updated: number; skipped: number }> {
  const { data } = await moneefloApi.patch("/reports/payout/mark", body);
  return data;
}
